import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';

import { FsDolpShare } from '@lib/api/entities';
import { SnCommonModule } from '@lib/common';
import { snDetailExpand, snFadeIn, snFadeOut, snListStagger } from '@lib/common/animations';
import { SnTableBaseComponent } from '@lib/core';
import { SnFormCheckboxModule } from '@lib/form/checkbox';
import { TranslationModule } from '@lib/translation';
import { SnButtonModule } from '@lib/ui/button';
import { SnDialogModule } from '@lib/ui/dialog';

import { Dolp } from '../../types/dolp';
import { DolpShareComponent } from '../dolp-share/dolp-share.component';

@Component({
  selector: 'sn-dolp-share-table',
  templateUrl: './dolp-share-table.component.html',
  styleUrls: ['./dolp-share-table.component.scss'],
  animations: [snListStagger, snFadeIn, snFadeOut, snDetailExpand],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SnFormCheckboxModule,
    SnDialogModule,
    MatRippleModule,
    MatTableModule,
    SnCommonModule,
    MatSortModule,
    SnButtonModule,
    TranslationModule
  ],
})
export class DolpShareTableComponent
  extends SnTableBaseComponent<FsDolpShare>
  implements OnDestroy, OnInit
{
  @Input() public originalAccess?: Dolp;

  public canView: string[] = [];
  public canEdit: string[] = [];
  public isAdmin: string[] = [];
  public canViewAll: boolean = false;
  public canEditAll: boolean = false;

  private readonly _destroy$ = new Subject();

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _dialogRef: MatDialogRef<DolpShareComponent>,
  ) {
    super();
    this.items = cloneDeep(this.items);
  }

  public ngOnInit(): void {
    this._handleDolpLoaded();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackBy(index: number, item: FsDolpShare): any {
    return item.id;
  }

  public controlActions(e: MatCheckboxChange, id: string, action: string): void {
    const changedBoxID = this.items.findIndex((u) => u.id === id);
    if (action === 'view') {
      this.items[changedBoxID].canView = e.checked;
      if (this.items[changedBoxID].canView) {
        this.canView.push(id);
      } else {
        const index = this.canView.indexOf(id);
        if (index > -1) {
          this.canView.splice(index, 1);
          this.canViewAll = false;
        }
      }
    }

    if (action === 'edit') {
      this.items[changedBoxID].canEdit = e.checked;
      if (this.items[changedBoxID].canEdit) {
        this.canEdit.push(id);
      } else {
        const index = this.canEdit.indexOf(id);
        if (index > -1) {
          this.canEdit.splice(index, 1);
          this.canEditAll = false;
        }
      }
    }

    if (action === 'admin') {
      this.items[changedBoxID].isAdmin = e.checked;
      if (this.items[changedBoxID].isAdmin) {
        this.isAdmin.push(id);
      } else {
        const index = this.isAdmin.indexOf(id);
        if (index > -1) {
          this.isAdmin.splice(index, 1);
        }
      }
    }
  }

  public save(): void {
    const data = {
      users: {
        editor: this.canEdit,
        viewer: this.canView,
        admin: this.isAdmin,
      },
    };
    this._dialogRef.close(data);
  }

  public toggleAllViews(event: any): void {
    if (event.checked) {
      this.items = this.items.map((item) => {
        item.canView = event.checked;
        this.canView.push(item.id);
        return item;
      });
    }
    if (!event.checked) {
      this.canView = [];
      this.items = this.items.map((item) => {
        item.canView = event.checked;
        return item;
      });
    }
    this._cd.detectChanges();
  }

  public toggleAllEdits(event: any): void {
    if (event.checked) {
      this.items = this.items.map((item) => {
        item.canEdit = event.checked;
        this.canEdit.push(item.id);
        return item;
      });
    }
    if (!event.checked) {
      this.canEdit = [];
      this.items = this.items.map((item) => {
        item.canEdit = event.checked;
        return item;
      });
    }
    this._cd.detectChanges();
  }

  private _handleDolpLoaded(): void {
    const data = this.originalAccess;

    this.canEdit = cloneDeep(data?.users?.editor?.map((u) => u.id)) || [];
    this.canView = cloneDeep(data?.users?.viewer?.map((u) => u.id)) || [];
    this.isAdmin = cloneDeep(data?.users?.admin?.map((u) => u.id)) || [];

    this.items.length === this.canView.length
      ? (this.canViewAll = true)
      : (this.canViewAll = false);

    this.items.length === this.canEdit.length
      ? (this.canEditAll = true)
      : (this.canEditAll = false);

    this.items.forEach((user) => {
      if (this.canEdit && this.canEdit.filter((canEdit) => canEdit === user.id).length > 0) {
        user.canEdit = true;
      }

      if (this.canView && this.canView.filter((canView) => canView === user.id).length > 0) {
        user.canView = true;
      }

      if (this.isAdmin && this.isAdmin.filter((isAdmin) => isAdmin === user.id).length > 0) {
        user.isAdmin = true;
      }
    });
  }
}
